import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Chip, TextField, CircularProgress,
  Select, MenuItem, InputLabel, FormControl, Paper, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import ApiIcon from '@mui/icons-material/Api';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import ViewListIcon from '@mui/icons-material/ViewList';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { fmtFechaHora } from '../utils/fechaUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return String(u.company_id || '');
  } catch { return ''; }
}

// Color por tipo de acción
const ACCION_COLOR = {
  voucher_aprobado: { bg: '#d1fae5', fg: '#065f46' },
  voucher_rechazado: { bg: '#fee2e2', fg: '#991b1b' },
  reserva_creada_agencia: { bg: '#dbeafe', fg: '#1e40af' },
  reserva_creada_turista: { bg: '#dbeafe', fg: '#1e40af' },
  reserva_cancelada: { bg: '#fee2e2', fg: '#991b1b' },
  reserva_cancelada_cupo: { bg: '#fed7aa', fg: '#9a3412' },
  reserva_estado_cambio: { bg: '#e0e7ff', fg: '#3730a3' },
  saldo_muelle: { bg: '#ccfbf1', fg: '#115e59' },
  saldo_cupo: { bg: '#ede9fe', fg: '#5b21b6' },
  saldo_cobrado_muelle: { bg: '#d1fae5', fg: '#065f46' },
  embarque: { bg: '#d1fae5', fg: '#065f46' },
  no_show: { bg: '#fee2e2', fg: '#991b1b' },
  timezone_cambiado: { bg: '#fef3c7', fg: '#92400e' },
  liquidacion_creada: { bg: '#ede9fe', fg: '#5b21b6' },
};

const ORIGEN_ICON = {
  frontend: <PersonIcon sx={{ fontSize: 14 }} />,
  chatbot_ia: <SmartToyIcon sx={{ fontSize: 14 }} />,
  sistema: <SettingsIcon sx={{ fontSize: 14 }} />,
  api: <ApiIcon sx={{ fontSize: 14 }} />,
};

// Etiqueta corta por tipo de entidad
const ENTIDAD_LABEL = {
  reserva: 'Reserva',
  voucher: 'Voucher',
  empresa: 'Empresa',
  embarque: 'Embarque',
  liquidacion: 'Liquidación',
};

export default function AdminTrazabilidad() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState('agrupada'); // 'agrupada' | 'plana'
  // Filtros iniciales pueden venir por URL (?fecha_desde=...&origen=muelle_app).
  // Permite que módulos como Muelle abran esta página con los filtros precargados
  // de la fecha activa, sin que el operador tenga que escribirlos.
  const _qp = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search) : null;
  const [filtros, setFiltros] = useState({
    accion: _qp?.get('accion') || '',
    origen: _qp?.get('origen') || '',
    // "usuario" es un identificador compuesto para el dropdown. Formato:
    //   "id:<id>", "email:<email>" o "nombre:<nombre>".
    // Se traduce a los filtros correctos al hacer el fetch.
    usuario: _qp?.get('usuario') || '',
    fecha_desde: _qp?.get('fecha_desde') || '',
    fecha_hasta: _qp?.get('fecha_hasta') || '',
    busqueda: _qp?.get('busqueda') || '',
    limit: 500,
  });
  const [catalogo, setCatalogo] = useState({ acciones: [], origenes: [] });
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [expandidos, setExpandidos] = useState({});
  const empresaId = getEmpresaId();

  const cargarCatalogo = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/turismo/auditoria/acciones-catalog`);
      const data = await res.json();
      setCatalogo(data);
    } catch (e) { console.error(e); }
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, limit: String(filtros.limit) });
      if (filtros.accion) params.append('accion', filtros.accion);
      if (filtros.origen) params.append('origen', filtros.origen);
      if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
      if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
      if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
      // El dropdown de usuario codifica el tipo de filtro en el prefijo.
      // Preferimos email/id sobre nombre porque son más precisos.
      if (filtros.usuario) {
        const [tipo, ...restoArr] = filtros.usuario.split(':');
        const val = restoArr.join(':');
        if (tipo === 'id')     params.append('usuario_id', val);
        if (tipo === 'email')  params.append('usuario_email', val);
        if (tipo === 'nombre') params.append('usuario_nombre', val);
      }
      const res = await fetch(`${API_BASE}/turismo/auditoria?${params}`);
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, filtros]);

  useEffect(() => { cargarCatalogo(); }, [cargarCatalogo]);
  useEffect(() => { cargar(); }, [cargar]);
  // Cargar usuarios que han generado actividad (para el dropdown de filtro).
  useEffect(() => {
    fetch(`${API_BASE}/turismo/auditoria/usuarios-con-actividad?empresa_id=${empresaId}&dias=365`)
      .then(r => r.json())
      .then(d => Array.isArray(d) && setUsuariosDisponibles(d))
      .catch(() => {});
  }, [empresaId]);

  const toggleExpand = (key) => setExpandidos(prev => ({ ...prev, [key]: !prev[key] }));

  // Agrupar items por entidad
  const grupos = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      // Llave de grupo: prefer entidad_codigo, luego entidad_tipo + id, luego entidad_tipo solo
      let key;
      if (it.entidad_codigo) key = `cod:${it.entidad_codigo}`;
      else if (it.entidad_tipo && it.entidad_id) key = `id:${it.entidad_tipo}:${it.entidad_id}`;
      else if (it.entidad_tipo) key = `tipo:${it.entidad_tipo}`;
      else key = `sin-entidad:${it.id}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          entidad_codigo: it.entidad_codigo,
          entidad_tipo: it.entidad_tipo,
          entidad_id: it.entidad_id,
          eventos: [],
        });
      }
      map.get(key).eventos.push(it);
    }
    // Dentro de cada grupo, ordenar eventos cronológicamente ascendente
    for (const g of map.values()) {
      g.eventos.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      g.ultimo = g.eventos[g.eventos.length - 1];
      g.primero = g.eventos[0];
      // Inferir contexto descriptivo del grupo
      const det = g.ultimo?.detalles || {};
      g.agencia = det.agencia_nombre || det.agencia;
      g.cliente = det.cliente_nombre || det.nombre_cliente;
    }
    // Lista de grupos ordenada por última actividad descendente
    return [...map.values()].sort((a, b) => new Date(b.ultimo.created_at) - new Date(a.ultimo.created_at));
  }, [items]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <HistoryIcon sx={{ fontSize: 36, color: '#0891b2' }} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#134e4a' }}>Trazabilidad</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            {vista === 'agrupada'
              ? `${grupos.length.toLocaleString('es-CO')} entidades · ${total.toLocaleString('es-CO')} eventos`
              : `${total.toLocaleString('es-CO')} eventos`}
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" value={vista} exclusive
          onChange={(e, v) => v && setVista(v)}>
          <ToggleButton value="agrupada" sx={{ textTransform: 'none', fontSize: 12 }}>
            <ViewAgendaIcon fontSize="small" sx={{ mr: 0.5 }} /> Agrupada
          </ToggleButton>
          <ToggleButton value="plana" sx={{ textTransform: 'none', fontSize: 12 }}>
            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} /> Plana
          </ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Refrescar">
          <IconButton onClick={cargar} sx={{ bgcolor: '#e0f2fe' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.95)' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr) auto' }, gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small">
            <InputLabel>Acción</InputLabel>
            <Select value={filtros.accion} label="Acción" onChange={e => setFiltros({ ...filtros, accion: e.target.value })}>
              <MenuItem value="">Todas</MenuItem>
              {catalogo.acciones.map(a => (
                <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Origen</InputLabel>
            <Select value={filtros.origen} label="Origen" onChange={e => setFiltros({ ...filtros, origen: e.target.value })}>
              <MenuItem value="">Todos</MenuItem>
              {catalogo.origenes.map(o => (
                <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Usuario</InputLabel>
            <Select value={filtros.usuario} label="Usuario"
              onChange={e => setFiltros({ ...filtros, usuario: e.target.value })}>
              <MenuItem value="">Todos</MenuItem>
              {usuariosDisponibles.map((u, idx) => {
                // Codificamos qué campo usar para filtrar según lo que tenga
                // el registro. Preferimos id → email → nombre.
                const val = u.usuario_id ? `id:${u.usuario_id}`
                          : u.email      ? `email:${u.email}`
                          : u.nombre     ? `nombre:${u.nombre}`
                          : '';
                if (!val) return null;
                const label = u.nombre || u.email || `id ${u.usuario_id}`;
                return (
                  <MenuItem key={`${val}-${idx}`} value={val}>
                    {label} <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 6 }}>
                      · {u.eventos || 0}
                    </span>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
            value={filtros.fecha_desde} onChange={e => setFiltros({ ...filtros, fecha_desde: e.target.value })} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
            value={filtros.fecha_hasta} onChange={e => setFiltros({ ...filtros, fecha_hasta: e.target.value })} />
          <TextField size="small" placeholder="Buscar código, usuario…"
            value={filtros.busqueda} onChange={e => setFiltros({ ...filtros, busqueda: e.target.value })}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 0.5, color: '#94a3b8' }} /> }}
            sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }} />
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <HistoryIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 1.5 }} />
          <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>
            Sin eventos registrados con los filtros actuales.
          </Typography>
        </Paper>
      ) : vista === 'agrupada' ? (
        // ═══ VISTA AGRUPADA ═══
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 12 } }}>
                <TableCell width={40}></TableCell>
                <TableCell>Entidad</TableCell>
                <TableCell>Contexto</TableCell>
                <TableCell align="center">Eventos</TableCell>
                <TableCell>Última acción</TableCell>
                <TableCell>Cuándo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grupos.map(g => {
                const isOpen = !!expandidos[g.key];
                const acc = ACCION_COLOR[g.ultimo.accion] || { bg: '#e5e7eb', fg: '#475569' };
                const accionLabel = (catalogo.acciones.find(a => a.key === g.ultimo.accion) || {}).label || g.ultimo.accion;
                const entidadLabel = ENTIDAD_LABEL[g.entidad_tipo] || g.entidad_tipo || '—';
                return (
                  <React.Fragment key={g.key}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleExpand(g.key)}>
                      <TableCell>
                        <IconButton size="small">
                          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {g.entidad_codigo
                            ? <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#134e4a' }}>{g.entidad_codigo}</Typography>
                            : <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#64748b' }}>{entidadLabel}{g.entidad_id ? ` #${g.entidad_id}` : ''}</Typography>}
                          <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>{entidadLabel}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {g.agencia && <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e40af' }}>🏢 {g.agencia}</Typography>}
                        {g.cliente && <Typography sx={{ fontSize: 11, color: '#475569' }}>👤 {g.cliente}</Typography>}
                        {!g.agencia && !g.cliente && <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={g.eventos.length} size="small" sx={{ fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1', minWidth: 32 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={accionLabel} size="small"
                          sx={{ bgcolor: acc.bg, color: acc.fg, fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap', color: '#475569' }}>{fmtFechaHora(g.ultimo.created_at)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: 0.5 }}>
                                LÍNEA DE TIEMPO — {g.eventos.length} EVENTO{g.eventos.length !== 1 ? 'S' : ''}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PictureAsPdfIcon fontSize="small" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const params = new URLSearchParams({ empresa_id: empresaId });
                                  if (g.entidad_codigo) params.append('entidad_codigo', g.entidad_codigo);
                                  else if (g.entidad_tipo && g.entidad_id) {
                                    params.append('entidad_tipo', g.entidad_tipo);
                                    params.append('entidad_id', String(g.entidad_id));
                                  }
                                  window.open(`${API_BASE}/turismo/auditoria/entidad-pdf?${params}`, '_blank');
                                }}
                                sx={{ textTransform: 'none', fontSize: 11, borderColor: '#dc2626', color: '#dc2626', '&:hover': { bgcolor: '#fef2f2', borderColor: '#991b1b' } }}
                              >
                                Descargar PDF
                              </Button>
                            </Box>
                            <Box sx={{ position: 'relative', pl: 3, '&::before': {
                              content: '""', position: 'absolute', left: 6, top: 8, bottom: 8, width: 2, bgcolor: '#cbd5e1',
                            } }}>
                              {g.eventos.map((ev, idx) => {
                                const evAcc = ACCION_COLOR[ev.accion] || { bg: '#e5e7eb', fg: '#475569' };
                                const evLabel = (catalogo.acciones.find(a => a.key === ev.accion) || {}).label || ev.accion;
                                const [detalleOpen, setDetalleOpen] = [!!expandidos[`det:${ev.id}`], () => toggleExpand(`det:${ev.id}`)];
                                return (
                                  <Box key={ev.id} sx={{ position: 'relative', mb: 1.5, '&:last-child': { mb: 0 } }}>
                                    <Box sx={{
                                      position: 'absolute', left: -20, top: 6, width: 14, height: 14,
                                      borderRadius: '50%', bgcolor: evAcc.fg, border: '3px solid #fff', boxShadow: '0 0 0 1px #cbd5e1',
                                    }} />
                                    <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                                        <Chip label={evLabel} size="small"
                                          sx={{ bgcolor: evAcc.bg, color: evAcc.fg, fontWeight: 600, fontSize: 10 }} />
                                        <Chip
                                          icon={ORIGEN_ICON[ev.origen]}
                                          label={ev.origen}
                                          size="small"
                                          sx={{ fontSize: 10, height: 20, bgcolor: '#f1f5f9', color: '#475569' }}
                                        />
                                        <Typography sx={{ fontSize: 11, color: '#94a3b8', ml: 'auto' }}>
                                          {fmtFechaHora(ev.created_at)}
                                        </Typography>
                                      </Box>
                                      {ev.descripcion && (
                                        <Typography sx={{ fontSize: 12, color: '#334155', mb: 0.5 }}>{ev.descripcion}</Typography>
                                      )}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                                          Por: <strong>{ev.usuario_nombre || (ev.origen === 'chatbot_ia' ? 'NARA IA' : ev.origen === 'sistema' ? 'Sistema' : '—')}</strong>
                                          {ev.usuario_email && <span style={{ color: '#94a3b8' }}> ({ev.usuario_email})</span>}
                                        </Typography>
                                        <Button size="small" onClick={(e) => { e.stopPropagation(); setDetalleOpen(); }}
                                          sx={{ textTransform: 'none', fontSize: 10, ml: 'auto' }}>
                                          {detalleOpen ? 'Ocultar JSON' : 'Ver JSON'}
                                        </Button>
                                      </Box>
                                      <Collapse in={detalleOpen} timeout="auto" unmountOnExit>
                                        <Box component="pre" sx={{ fontSize: 10, mt: 1, mb: 0, p: 1, bgcolor: '#f1f5f9', borderRadius: 1, whiteSpace: 'pre-wrap', fontFamily: 'monospace', color: '#334155' }}>
                                          {JSON.stringify(ev.detalles || {}, null, 2)}
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // ═══ VISTA PLANA (la original) ═══
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 12 } }}>
                <TableCell width={40}></TableCell>
                <TableCell>Fecha y hora</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Entidad</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Autor</TableCell>
                <TableCell>Origen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(it => {
                const isOpen = !!expandidos[`flat:${it.id}`];
                const acc = ACCION_COLOR[it.accion] || { bg: '#e5e7eb', fg: '#475569' };
                const accionLabel = (catalogo.acciones.find(a => a.key === it.accion) || {}).label || it.accion;
                return (
                  <React.Fragment key={it.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleExpand(`flat:${it.id}`)}>
                          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtFechaHora(it.created_at)}</TableCell>
                      <TableCell>
                        <Chip label={accionLabel} size="small"
                          sx={{ bgcolor: acc.bg, color: acc.fg, fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {it.entidad_codigo ? <strong>{it.entidad_codigo}</strong> : (it.entidad_tipo || '—')}
                        {it.entidad_id && <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>id: {it.entidad_id}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{it.descripcion || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {it.usuario_nombre || (it.origen === 'chatbot_ia' ? 'NARA IA' : it.origen === 'sistema' ? 'Sistema' : '—')}
                        {it.usuario_email && <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>{it.usuario_email}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip icon={ORIGEN_ICON[it.origen]} label={it.origen} size="small"
                          sx={{ fontSize: 10, height: 22, bgcolor: '#f1f5f9', color: '#475569' }} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', mb: 1 }}>DETALLES</Typography>
                            <Box component="pre" sx={{ fontSize: 11, m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', color: '#334155' }}>
                              {JSON.stringify(it.detalles || {}, null, 2)}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
